import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../config';

const StudentResourcesTab = ({ studentForm }) => {
  const [currentPath, setCurrentPath] = useState([]);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  // Determine if we're at root level or in a subfolder
  const isRootLevel = currentPath.length === 0;
  const thumbnailHeight = isRootLevel ? 'h-[200px]' : 'h-[150px]';

  useEffect(() => {
    if (studentForm) {
      loadResources();
    }
  }, [studentForm, currentPath]);

  const getCurrentFolderPath = () => {
    return currentPath.join('/');
  };

  const loadResources = async () => {
    try {
      setLoading(true);
      const folderPath = getCurrentFolderPath();
      const response = await axios.get(buildApiUrl(`/api/resources/list/${studentForm}${folderPath ? `?path=${encodeURIComponent(folderPath)}` : ''}`));
      if (response.data.success) {
        setFolders(response.data.folders || []);
        setFiles(response.data.files || []);
      }
    } catch (error) {
      console.error('Failed to load resources:', error);
      setFolders([]);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const openFolder = (folderName) => {
    setCurrentPath([...currentPath, folderName]);
  };

  const navigateUp = () => {
    if (currentPath.length > 0) {
      setCurrentPath(currentPath.slice(0, -1));
    }
  };

  const navigateToPath = (index) => {
    setCurrentPath(currentPath.slice(0, index + 1));
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    
    // Return appropriate emoji/icon for file type
    const iconMap = {
      // Documents
      'pdf': '📕',
      'doc': '📘',
      'docx': '📘',
      'txt': '📄',
      'rtf': '📄',
      
      // Spreadsheets
      'xls': '📗',
      'xlsx': '📗',
      'csv': '📊',
      
      // Presentations
      'ppt': '📙',
      'pptx': '📙',
      
      // Images
      'jpg': '🖼️',
      'jpeg': '🖼️',
      'png': '🖼️',
      'gif': '🖼️',
      'bmp': '🖼️',
      'svg': '🖼️',
      'webp': '🖼️',
      
      // Videos
      'mp4': '🎬',
      'mov': '🎬',
      'avi': '🎬',
      'mkv': '🎬',
      'wmv': '🎬',
      'flv': '🎬',
      'webm': '🎬',
      
      // Audio
      'mp3': '🎵',
      'wav': '🎵',
      'flac': '🎵',
      'aac': '🎵',
      'm4a': '🎵',
      
      // Archives
      'zip': '📦',
      'rar': '📦',
      '7z': '📦',
      'tar': '📦',
      'gz': '📦',
      
      // Code
      'js': '📜',
      'py': '📜',
      'java': '📜',
      'cpp': '📜',
      'html': '📜',
      'css': '📜',
      'json': '📜',
      'xml': '📜'
    };
    
    return iconMap[ext] || '📎';
  };

  const isImageFile = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext);
  };

  const isVideoFile = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    return ['mp4', 'mov', 'avi', 'mkv', 'wmv', 'flv', 'webm'].includes(ext);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFolderThumbnail = (folderName) => {
    const folderPath = getCurrentFolderPath();
    const fullPath = folderPath ? `${folderPath}/${folderName}` : folderName;
    return buildApiUrl(`/api/resources/thumbnail/${studentForm}?path=${encodeURIComponent(fullPath)}`);
  };

  if (!studentForm) {
    return (
      <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-300">
        <div className="text-6xl mb-4">⚠️</div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to Load Resources</h3>
        <p className="text-gray-600">Student form information not available</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header with Breadcrumb Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <span className="text-purple-600 text-xl">📚</span>
            </div>
          </div>
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 mt-2 text-sm ml-14">
            <button
              onClick={() => setCurrentPath([])}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              📁 Root
            </button>
            {currentPath.map((folder, index) => (
              <React.Fragment key={index}>
                <span className="text-gray-400">/</span>
                <button
                  onClick={() => navigateToPath(index)}
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  {folder}
                </button>
              </React.Fragment>
            ))}
          </div>
        </div>
        {currentPath.length > 0 && (
          <button
            onClick={navigateUp}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && folders.length === 0 && files.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border-2 border-dashed border-gray-300">
          <div className="text-6xl mb-4">📁</div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {currentPath.length > 0 ? 'Folder is Empty' : 'No Resources Available'}
          </h3>
          <p className="text-gray-600 mb-4">
            {currentPath.length > 0 
              ? 'This folder doesn\'t contain any files or subfolders yet'
              : 'Your teachers haven\'t added any resources for your class yet'}
          </p>
        </div>
      )}

      {/* Folders and Files Grid */}
      {!loading && (folders.length > 0 || files.length > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {/* Folders */}
          {folders.map((folder) => (
            <div
              key={folder.name}
              onDoubleClick={() => openFolder(folder.name)}
              className="cursor-pointer group"
            >
              {/* Thumbnail or Folder Icon */}
              <div className={`w-full ${thumbnailHeight} bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center relative rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg hover:border-blue-300 transition-all`}>
                <img 
                  src={getFolderThumbnail(folder.name)}
                  alt={folder.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-full h-full absolute inset-0 flex items-center justify-center" style={{display: 'none'}}>
                  <span className="text-6xl">📁</span>
                </div>
              </div>
              {/* Folder Info - Outside thumbnail */}
              <div className="mt-2 text-center">
                <p className="text-base font-medium text-gray-900 truncate" title={folder.name}>
                  {folder.name}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {folder.item_count || 0} items
                </p>
              </div>
            </div>
          ))}

          {/* Files */}
          {files.map((file, idx) => (
            <div
              key={idx}
              className="group relative"
            >
              <a
                href={buildApiUrl(file.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <div className={`w-full ${thumbnailHeight} bg-gray-50 flex items-center justify-center relative overflow-hidden rounded-lg border border-gray-200 hover:shadow-lg hover:border-blue-300 transition-all`}>
                  {isImageFile(file.name) ? (
                    <img 
                      src={buildApiUrl(file.url)}
                      alt={file.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : isVideoFile(file.name) ? (
                    <video 
                      src={buildApiUrl(file.url)}
                      className="w-full h-full object-cover"
                      muted
                      preload="metadata"
                    />
                  ) : null}
                  <div className={`${isImageFile(file.name) || isVideoFile(file.name) ? 'absolute inset-0 bg-gradient-to-t from-black/50 to-transparent' : ''} flex items-center justify-center w-full h-full`} style={{ display: isImageFile(file.name) || isVideoFile(file.name) ? 'none' : 'flex' }}>
                    <div className="text-5xl">{getFileIcon(file.name)}</div>
                  </div>
                </div>
              </a>
              {/* File Info - Outside thumbnail */}
              <div className="mt-2 text-center">
                <p className="text-base font-medium text-gray-900 truncate" title={file.name}>
                  {file.name}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">{formatFileSize(file.size)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </div>
  );
};

export default StudentResourcesTab;
